import history from '@history';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';

import {
  LOAN_APPLICATION_ACTION_NAME_PREFIX,
  LoanApplicationRestrictionMessage,
} from 'constants/loanApplication';
import { checkIsAppLoadedInIframe } from 'utils/iframe';
import { createNamedActions } from 'utils/namedActionTypesProxy';
import { getUUID } from 'utils/uuid';

import { showMessageAction } from 'store/global/actions';

export const CONTROL_UI_ACTION_TYPE = createNamedActions(
  // LOAN_APPLICATION_ACTION_NAME_PREFIX is used for Redux Middleware in Loan Editor
  `${LOAN_APPLICATION_ACTION_NAME_PREFIX}/controlUI`,
  {
    TOGGLE_EDIT_STATE: 'TOGGLE_EDIT_STATE',
    CLEAR_EDITING_IDS: 'CLEAR_EDITING_IDS',
    SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
    SET_AVAILABLE_TABS: 'SET_AVAILABLE_TABS',
    SET_IS_ACTIVE_TAB_RE_FETCH: 'SET_IS_ACTIVE_TAB_RE_FETCH',
    SET_IS_SIGNAL_R_INITIATED: 'SET_IS_SIGNAL_R_INITIATED',
    OPEN_DOCUMENT_PANE: 'OPEN_DOCUMENT_PANE',
    CLOSE_DOCUMENT_PANE: 'CLOSE_DOCUMENT_PANE',
    SET_DOCUMENT_PANE_SIZES: 'SET_DOCUMENT_PANE_SIZES',
    SET_DOCUMENT_PANE_HOST_WIDTH: 'SET_DOCUMENT_PANE_HOST_WIDTH',
  },
);

export const toggleEditStateAction = id => dispatch => {
  return dispatch({
    type: CONTROL_UI_ACTION_TYPE.TOGGLE_EDIT_STATE,
    payload: id,
  });
};

export const clearEditStateAction = tabName => ({
  type: CONTROL_UI_ACTION_TYPE.CLEAR_EDITING_IDS,
  meta: { tabName },
});

const setActiveTabAction = tab => ({
  type: CONTROL_UI_ACTION_TYPE.SET_ACTIVE_TAB,
  payload: tab,
});

const setActiveTabReFetchData = reFetchData => ({
  type: CONTROL_UI_ACTION_TYPE.SET_IS_ACTIVE_TAB_RE_FETCH,
  payload: reFetchData,
});

export const setActiveTabWithNavigationAction =
  ({
    activeTab,
    activeSubtab,
    loanId,
    applicationId,
    entityId,
    section,
    subsection,
    searchParams,
  }) =>
  dispatch => {
    const isNavigationDisabled = checkIsAppLoadedInIframe();

    if (!isNavigationDisabled) {
      const optionalParams = {
        ...(!_.isNil(activeSubtab)
          ? { [LOANS_ROUTING_PARAMS.subtab]: _.kebabCase(activeSubtab) }
          : {}),
        ...(!_.isNil(entityId) ? { [LOANS_ROUTING_PARAMS.entityId]: entityId } : {}),
        ...(!_.isNil(section) ? { [LOANS_ROUTING_PARAMS.section]: section } : {}),
        ...(!_.isNil(subsection) ? { [LOANS_ROUTING_PARAMS.subsection]: subsection } : {}),
      };

      const loanPath = loanId
        ? LoansPaths.loanApplicationManagement.generatePath({
            loanId,
            [LOANS_ROUTING_PARAMS.tab]: _.kebabCase(activeTab),
            ...optionalParams,
          })
        : undefined;

      const pathname = applicationId
        ? LoansPaths.applicationManagement.generatePath({
            applicationId,
            [LOANS_ROUTING_PARAMS.tab]: _.kebabCase(activeTab),
            ...optionalParams,
          })
        : loanPath;

      const href = history.createHref({
        pathname,
        search: new URLSearchParams(searchParams).toString(),
      });

      history.push(href);
    }

    return dispatch(setActiveTabAction(activeTab));
  };

export const remountActiveTabAction =
  ({ tabsToRemount, subtabsToRemount, searchParams, triggeredByPendingChanges = false } = {}) =>
  dispatch => {
    const { params } = LoansPaths.applicationManagement.matchPath(history.location.pathname) || {};
    const { subtab } = searchParams || {};
    const activeTabId = params?.tab;

    const isSubtabToRemountOmitted =
      !!subtabsToRemount?.length && !subtabsToRemount.includes(subtab);
    const isTabToRemountOmitted =
      !!tabsToRemount?.length && !tabsToRemount.includes(activeTabId) && isSubtabToRemountOmitted;

    if (isTabToRemountOmitted) {
      return;
    }

    dispatch(
      setActiveTabReFetchData({ isActiveTabReFetchNeeded: true, triggeredByPendingChanges }),
    );
    setTimeout(
      () =>
        dispatch(
          setActiveTabReFetchData({
            isActiveTabReFetchNeeded: false,
            triggeredByPendingChanges: false,
          }),
        ),
      0,
    );
  };

export const setAvailableTabsAction = tabs => ({
  type: CONTROL_UI_ACTION_TYPE.SET_AVAILABLE_TABS,
  payload: tabs,
});

export const showMessageThatCurrentUserCantChangeLoanAction = () => dispatch => {
  dispatch(
    showMessageAction({
      message: LoanApplicationRestrictionMessage.CURRENT_USER_CANT_CHANGE_LOAN,
      variant: 'error',
    }),
  );
};

export const setIsSignalRInitiatedAction = payload => ({
  type: CONTROL_UI_ACTION_TYPE.SET_IS_SIGNAL_R_INITIATED,
  payload,
});

export const openDocumentPaneAction = () => ({
  type: CONTROL_UI_ACTION_TYPE.OPEN_DOCUMENT_PANE,
  payload: { paneId: getUUID() },
});

export const closeDocumentPaneAction = paneId => ({
  type: CONTROL_UI_ACTION_TYPE.CLOSE_DOCUMENT_PANE,
  payload: paneId,
});

export const setDocumentPaneSizesAction = sizes => ({
  type: CONTROL_UI_ACTION_TYPE.SET_DOCUMENT_PANE_SIZES,
  payload: sizes,
});

export const setDocumentPaneHostWidthAction = width => ({
  type: CONTROL_UI_ACTION_TYPE.SET_DOCUMENT_PANE_HOST_WIDTH,
  payload: width,
});
